import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine, faTimes, faUserFriends, faHardHat, faCalendarWeek
} from '@fortawesome/free-solid-svg-icons';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const TimesheetStatsModal = ({
    showStats,
    setShowStats,
    chartData,
    entries
}) => {
    if (!showStats) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1001,
            padding: '2rem',
            overflowY: 'auto'
        }}>
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 'var(--border-radius-xl)',
                maxWidth: '1200px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: 'var(--shadow-xl)'
            }}>
                {/* Header */}
                <div style={{
                    padding: '2rem 2rem 1rem',
                    borderBottom: '2px solid var(--color-light-gray)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'sticky',
                    top: 0,
                    background: 'var(--color-surface)',
                    zIndex: 1
                }}>
                    <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FontAwesomeIcon icon={faChartLine} style={{ color: '#8b5cf6' }} />
                        Statistiques & Graphiques
                    </h2>
                    <button
                        onClick={() => setShowStats(false)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.5rem',
                            color: 'var(--color-text-light)',
                            padding: '8px'
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Charts Grid */}
                <div style={{ padding: '2rem' }}>
                    {/* Row 1: Employee Bar Chart & Chantier Pie Chart */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
                        gap: '2rem',
                        marginBottom: '2rem'
                    }}>
                        {/* Employee Hours Chart */}
                        <div style={{
                            background: 'var(--color-background)',
                            padding: '1.5rem',
                            borderRadius: 'var(--border-radius-lg)',
                            border: '1px solid var(--color-light-gray)'
                        }}>
                            <h3 style={{
                                margin: '0 0 1.5rem',
                                fontSize: '1.1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <FontAwesomeIcon icon={faUserFriends} style={{ color: '#3b82f6' }} />
                                Heures par Employé
                            </h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={chartData.employeeData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="name" stroke="var(--color-text-light)" />
                                    <YAxis stroke="var(--color-text-light)" />
                                    <Tooltip
                                        contentStyle={{
                                            background: 'var(--color-surface)',
                                            border: '1px solid var(--color-light-gray)',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Legend />
                                    <Bar dataKey="hours" fill="#3b82f6" name="Heures" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Chantier Distribution Pie Chart */}
                        <div style={{
                            background: 'var(--color-background)',
                            padding: '1.5rem',
                            borderRadius: 'var(--border-radius-lg)',
                            border: '1px solid var(--color-light-gray)'
                        }}>
                            <h3 style={{
                                margin: '0 0 1.5rem',
                                fontSize: '1.1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <FontAwesomeIcon icon={faHardHat} style={{ color: '#10b981' }} />
                                Répartition par Chantier
                            </h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={chartData.chantierData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {chartData.chantierData.map((entry, index) => {
                                            const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
                                            return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                                        })}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{
                                            background: 'var(--color-surface)',
                                            border: '1px solid var(--color-light-gray)',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Row 2: Daily Trend Line Chart */}
                    <div style={{
                        background: 'var(--color-background)',
                        padding: '1.5rem',
                        borderRadius: 'var(--border-radius-lg)',
                        border: '1px solid var(--color-light-gray)',
                        marginBottom: '2rem'
                    }}>
                        <h3 style={{
                            margin: '0 0 1.5rem',
                            fontSize: '1.1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <FontAwesomeIcon icon={faChartLine} style={{ color: '#8b5cf6' }} />
                            Tendance - 30 derniers jours
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={chartData.dailyData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="date" stroke="var(--color-text-light)" />
                                <YAxis stroke="var(--color-text-light)" />
                                <Tooltip
                                    contentStyle={{
                                        background: 'var(--color-surface)',
                                        border: '1px solid var(--color-light-gray)',
                                        borderRadius: '8px'
                                    }}
                                />
                                <Legend />
                                <Line type="monotone" dataKey="hours" stroke="#8b5cf6" strokeWidth={2} name="Heures" />
                                <Line type="monotone" dataKey="entries" stroke="#f59e0b" strokeWidth={2} name="Entrées" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Row 3: Weekly Distribution Bar Chart */}
                    <div style={{
                        background: 'var(--color-background)',
                        padding: '1.5rem',
                        borderRadius: 'var(--border-radius-lg)',
                        border: '1px solid var(--color-light-gray)'
                    }}>
                        <h3 style={{
                            margin: '0 0 1.5rem',
                            fontSize: '1.1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <FontAwesomeIcon icon={faCalendarWeek} style={{ color: '#10b981' }} />
                            Distribution hebdomadaire
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={chartData.weekData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="day" stroke="var(--color-text-light)" />
                                <YAxis stroke="var(--color-text-light)" />
                                <Tooltip
                                    contentStyle={{
                                        background: 'var(--color-surface)',
                                        border: '1px solid var(--color-light-gray)',
                                        borderRadius: '8px'
                                    }}
                                />
                                <Legend />
                                <Bar dataKey="hours" fill="#10b981" name="Heures totales" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Summary Stats */}
                    <div style={{
                        marginTop: '2rem',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '1rem'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: 'white',
                            padding: '1.5rem',
                            borderRadius: 'var(--border-radius-lg)',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                {chartData.employeeData.length}
                            </div>
                            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Employés actifs</div>
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            padding: '1.5rem',
                            borderRadius: 'var(--border-radius-lg)',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                {chartData.chantierData.length}
                            </div>
                            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Chantiers</div>
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: 'white',
                            padding: '1.5rem',
                            borderRadius: 'var(--border-radius-lg)',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                {entries.reduce((sum, e) => sum + (e.hours || 0), 0)}h
                            </div>
                            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Total heures</div>
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                            color: 'white',
                            padding: '1.5rem',
                            borderRadius: 'var(--border-radius-lg)',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                {chartData.employeeData.length > 0 ? (Math.round((entries.reduce((sum, e) => sum + (e.hours || 0), 0) / chartData.employeeData.length) * 10) / 10) : 0}h
                            </div>
                            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Moyenne/Employé</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimesheetStatsModal;
